/**
 * Hook: useResourceLimits
 * Descrição: Gerencia validação de limites de recursos e add-ons do tenant
 * 
 * Funcionalidades:
 * - Busca limites efetivos do tenant (plano base + add-ons)
 * - Valida criação de novos recursos (agentes, inboxes, etc.)
 * - Verifica disponibilidade de canais e provedores
 * - Verifica acesso a módulos (campanhas, WhatsApp Oficial)
 * - Suporta preços diferenciados por provider
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { showToast } from "@/lib/toast";
import { logger } from "@/lib/logger";

// Interface para limites efetivos retornados pela RPC
interface EffectiveLimits {
  tenant_id: string;
  plan_id: string;
  plan_name: string;
  
  // Limites base do plano
  base_max_agents: number;
  base_max_inboxes: number;
  base_max_instances: number;
  base_max_channels: number;
  base_max_messages: number;
  
  // Add-ons contratados
  addon_agents: number;
  addon_inboxes: number;
  addon_instances: number;
  addon_channels: number;
  
  // Limites efetivos (base + add-ons)
  effective_max_agents: number;
  effective_max_inboxes: number;
  effective_max_instances: number;
  effective_max_channels: number;
  effective_max_messages: number;
  
  // Módulos e recursos especiais
  has_campaigns_module: boolean;
  has_official_whatsapp: boolean;
  has_digital_contracts_module: boolean;  // Módulo contrato digital
  has_financial_module: boolean;  // Módulo financeiro (add-on modulo_financeiro)
  is_enterprise: boolean;
  
  // Arrays de recursos disponíveis
  available_channel_types: string[];
  available_providers: string[];
  
  // Metadata
  calculated_at: string;
  error?: string;
}

// Interface para validação de criação de recurso
interface ResourceValidation {
  can_create: boolean;
  error_message: string | null;
  current_count: number | null;
  max_allowed: number | null;
}

// Interface para add-on disponível (via RPC get_available_addons_safe)
interface AvailableAddon {
  id_short: string;
  addon_id_full: string;  // UUID completo para uso na mutation de compra
  slug: string;
  name: string;
  description: string | null;
  addon_type: string;
  unit_value: number | null;
  price_monthly: number | null;
  price_yearly: number | null;
  setup_fee: number | null;
  max_quantity: number | null;
  min_plan_required: string | null;
  available_plans: string[] | null;
  is_active: boolean;
  // Campos específicos do módulo Indicação (v1.1.0 da RPC get_available_addons_safe)
  max_referral_campaigns?: number | null;
  max_referral_programs?: number | null;
  max_referral_affiliates?: number | null;
  referral_bonus_credits?: number | null;
  conversion_credit_quota?: number | null;
}

// Interface para preço de add-on por provider (via RPC get_addon_provider_prices_safe)
interface AddonProviderPrice {
  addon_id_short: string;
  provider_id_short: string;
  provider_name: string;
  provider_slug: string;
  price_monthly: number;
  price_yearly: number | null;
  setup_fee: number;
  is_active: boolean;
}

// Interface para add-on contratado pelo tenant (via RPC get_my_tenant_addons)
interface TenantAddon {
  id_short: string;
  id_full: string;  // UUID completo para operações (cancelamento)
  addon_name: string;
  addon_slug: string;
  addon_type: string;
  addon_description: string | null;
  addon_unit_value: number | null;
  addon_price_monthly: number | null;
  addon_max_quantity: number | null;
  quantity: number;
  status: string;
  activated_at: string | null;
  cancelled_at: string | null;
  expires_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  provider_id_short: string | null;
  created_at: string;
}

export function useResourceLimits() {
  const { currentTenant, user } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  // Buscar limites efetivos do tenant
  const {
    data: limits,
    isLoading: isLoadingLimits,
    error: limitsError,
    refetch: refetchLimits,
  } = useQuery({
    queryKey: ["resource-limits", tenantId],
    queryFn: async () => {
      if (!tenantId) return null;

      const { data, error } = await supabase.rpc("get_tenant_effective_limits", {
        p_tenant_id: tenantId,
      });

      if (error) {
        logger.error("[useResourceLimits] Erro ao buscar limites:", error.message);
        throw error;
      }

      // RPC retorna JSONB, fazer cast via unknown
      return data as unknown as EffectiveLimits;
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // Cache por 5 minutos
    refetchOnWindowFocus: false,
  });

  // Buscar add-ons disponíveis via RPC segura (sem Stripe IDs)
  const {
    data: availableAddons,
    isLoading: isLoadingAddons,
  } = useQuery({
    queryKey: ["available-addons", limits?.plan_id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_available_addons_safe", {
        p_tenant_hint: null,
      });

      if (error) {
        logger.error("[useResourceLimits] Erro ao buscar add-ons:", error.message);
        throw error;
      }

      // Filtrar por planos disponíveis
      const planId = limits?.plan_id || "free";
      return ((data || []) as unknown as AvailableAddon[]).filter((addon) => {
        if (!addon.available_plans) return true;
        return addon.available_plans.includes(planId);
      });
    },
    enabled: !!limits,
    staleTime: 10 * 60 * 1000,
  });

  // Buscar preços por provider via RPC segura (sem Stripe IDs)
  const {
    data: addonProviderPrices,
    isLoading: isLoadingProviderPrices,
  } = useQuery({
    queryKey: ["addon-provider-prices"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_addon_provider_prices_safe", {
        p_tenant_hint: null,
      });

      if (error) {
        logger.error("[useResourceLimits] Erro ao buscar preços por provider:", error.message);
        throw error;
      }

      return (data || []) as unknown as AddonProviderPrice[];
    },
    staleTime: 10 * 60 * 1000,
  });

  // Buscar add-ons contratados pelo tenant via RPC segura
  const {
    data: tenantAddons,
    isLoading: isLoadingTenantAddons,
    refetch: refetchTenantAddons,
  } = useQuery({
    queryKey: ["tenant-addons", tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      const { data, error } = await supabase.rpc("get_my_tenant_addons", {
        p_tenant_hint: null,
      });

      if (error) {
        logger.error("[useResourceLimits] Erro ao buscar add-ons do tenant:", error.message);
        throw error;
      }

      return (data || []) as unknown as TenantAddon[];
    },
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000,
  });

  // Validar criação de recurso
  const validateResourceCreation = async (
    resourceType: "agent" | "inbox" | "instance" | "campaign",
    channelTypeId?: string,
    providerId?: string
  ): Promise<ResourceValidation> => {
    if (!tenantId) {
      return {
        can_create: false,
        error_message: "Tenant não identificado",
        current_count: null,
        max_allowed: null,
      };
    }

    try {
      const { data, error } = await supabase.rpc("validate_resource_creation", {
        p_tenant_id: tenantId,
        p_resource_type: resourceType,
        p_channel_type_id: channelTypeId || null,
        p_provider_id: providerId || null,
      });

      if (error) {
        logger.error("[useResourceLimits] Erro na validação:", error.message);
        return {
          can_create: false,
          error_message: error.message,
          current_count: null,
          max_allowed: null,
        };
      }

      return data as unknown as ResourceValidation;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      logger.error("[useResourceLimits] Erro inesperado:", errorMessage);
      return {
        can_create: false,
        error_message: "Erro ao validar limites",
        current_count: null,
        max_allowed: null,
      };
    }
  };

  // Helper para obter preço de add-on por provider (usando dados truncados)
  const getAddonPriceForProvider = (addonIdShort: string, providerIdShort: string): AddonProviderPrice | null => {
    if (!addonProviderPrices) return null;
    return addonProviderPrices.find(
      (p) => p.addon_id_short === addonIdShort && p.provider_id_short === providerIdShort
    ) || null;
  };

  // Mutation para comprar add-on — provider_id resolvido server-side na edge function
  const purchaseAddon = useMutation({
    mutationFn: async ({ 
      addonId, 
      quantity = 1 
    }: { 
      addonId: string;  // UUID completo do addon (addon_id_full)
      quantity?: number;
    }) => {
      const { data, error } = await supabase.functions.invoke("addon-checkout", {
        body: {
          tenant_id: tenantId,
          addon_id: addonId,
          quantity,
        },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);
      
      return data;
    },
    onSuccess: (data) => {
      if (data.url) {
        // Abrir checkout em nova aba
        window.open(data.url, "_blank");
        showToast.info("Redirecionando para checkout do add-on...");
      }
    },
    onError: (error: Error) => {
      logger.error("[useResourceLimits] Erro ao comprar add-on:", error.message);
      showToast.error(`Erro ao iniciar compra: ${error.message}`);
    },
  });

  // Mutation para cancelar add-on (total ou parcial via newQuantity)
  // newQuantity: quando fornecido (>0 e < quantity atual), reduz a quantidade no Stripe
  //              em vez de cancelar a subscription inteira. Útil para task_pack/task_storage
  //              e para qualquer add-on "quantity-based".
  const cancelAddon = useMutation({
    mutationFn: async ({
      tenantAddonId,
      cancelImmediately = false,
      newQuantity,
    }: {
      tenantAddonId: string;
      cancelImmediately?: boolean;
      newQuantity?: number;
    }) => {
      const { data, error } = await supabase.functions.invoke("addon-cancel", {
        body: {
          tenant_id: tenantId,
          tenant_addon_id: tenantAddonId,
          cancel_immediately: cancelImmediately,
          ...(typeof newQuantity === "number" ? { new_quantity: newQuantity } : {}),
        },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);
      
      return data;
    },
    onSuccess: (data) => {
      showToast.success(data.message || "Add-on cancelado com sucesso");
      // Invalidar caches
      queryClient.invalidateQueries({ queryKey: ["tenant-addons", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["resource-limits", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["tasks-usage", tenantId] });
    },
    onError: (error: Error) => {
      logger.error("[useResourceLimits] Erro ao cancelar add-on:", error.message);
      showToast.error(`Erro ao cancelar: ${error.message}`);
    },
  });

  // Helpers para verificações rápidas
  const canCreateAgent = limits 
    ? limits.effective_max_agents > 0 || limits.is_enterprise 
    : false;

  const canCreateInbox = limits 
    ? limits.effective_max_inboxes > 0 || limits.is_enterprise 
    : false;

  const hasCampaignsModule = limits?.has_campaigns_module || false;
  
  const hasOfficialWhatsApp = limits?.has_official_whatsapp || false;

  // Flag do módulo contrato digital (add-on contrato_digital)
  const hasDigitalContractsModule = limits?.has_digital_contracts_module || false;

  // Flag do módulo financeiro (add-on modulo_financeiro)
  const hasFinancialModule = limits?.has_financial_module || false;

  const isEnterprise = limits?.is_enterprise || false;

  // Verificar se canal está disponível
  const isChannelAvailable = (channelTypeId: string): boolean => {
    if (!limits?.available_channel_types) return false;
    return limits.available_channel_types.includes(channelTypeId);
  };

  // Verificar se provedor está disponível
  const isProviderAvailable = (providerId: string): boolean => {
    if (!limits?.available_providers) return false;
    return limits.available_providers.includes(providerId);
  };

  // Calcular uso atual de recursos
  const getResourceUsage = async (resourceType: "agents" | "inboxes" | "channels") => {
    if (!tenantId) return { current: 0, max: 0, percentage: 0 };

    let currentCount = 0;
    let maxCount = 0;

    switch (resourceType) {
      case "agents": {
        const { count } = await supabase
          .from("tenant_members")
          .select("*", { count: "exact", head: true })
          .eq("tenant_id", tenantId);
        currentCount = count || 0;
        maxCount = limits?.effective_max_agents || 0;
        break;
      }
      case "inboxes": {
        // Usa RPC count_active_inboxes para contar inboxes não-suspensas
        const { data: countData, error } = await supabase.rpc("count_active_inboxes", {
          p_tenant_id: tenantId,
        });
        currentCount = error ? 0 : (countData || 0);
        maxCount = limits?.effective_max_inboxes || 0;
        break;
      }
      case "channels": {
        const { data } = await supabase
          .from("inboxes")
          .select("channel_type_id")
          .eq("tenant_id", tenantId)
          .eq("status", "active");
        const uniqueChannels = new Set(data?.map((i) => i.channel_type_id) || []);
        currentCount = uniqueChannels.size;
        maxCount = limits?.effective_max_channels || 0;
        break;
      }
    }

    // Enterprise não tem limites
    if (limits?.is_enterprise) {
      return { current: currentCount, max: Infinity, percentage: 0 };
    }

    const percentage = maxCount > 0 ? Math.round((currentCount / maxCount) * 100) : 0;
    return { current: currentCount, max: maxCount, percentage };
  };

  return {
    // Dados
    limits,
    availableAddons,
    addonProviderPrices,
    tenantAddons,
    
    // Estados de loading
    isLoading: isLoadingLimits || isLoadingAddons || isLoadingTenantAddons || isLoadingProviderPrices,
    isLoadingLimits,
    isLoadingAddons,
    isLoadingProviderPrices,
    isLoadingTenantAddons,
    
    // Erros
    error: limitsError,
    
    // Ações
    refetchLimits,
    refetchTenantAddons,
    validateResourceCreation,
    purchaseAddon: purchaseAddon.mutateAsync,
    cancelAddon: cancelAddon.mutateAsync,
    isPurchasing: purchaseAddon.isPending,
    isCanceling: cancelAddon.isPending,
    
    // Helpers
    canCreateAgent,
    canCreateInbox,
    hasCampaignsModule,
    hasOfficialWhatsApp,
    hasDigitalContractsModule,
    hasFinancialModule,
    isEnterprise,
    isChannelAvailable,
    isProviderAvailable,
    getResourceUsage,
    getAddonPriceForProvider,
  };
}
